import { DEMO_CALCULATOR_STEP } from 'config/constants'
import React, {useEffect, useState} from "react";
import cx from 'classnames';
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
  clearCalculationViewModeEditAttempt,
  useCalculationViewModeEditAttempt,
  useCurrentRoute,
  useGetOrderSummaryByIdQuery,
  useLazyGetOrderSummaryInvoicesByIdQuery,
  useOrder,
  useRefreshCurrencyRatesMutation,
  useSummaryCompleted,
} from 'api/calc'

import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { ThemeProvider } from '@mui/material/styles';

import Main from './components/main';
import CalculatorDisclaimer from './disclaimer';
import InvoicesAndProducts from './components/invoices-and-products';
import Delivery from './components/delivery';
import Customs from './components/customs';
import Additional from './components/additional';
import Summary from './components/summary';
import BankCommission from './components/bank-commission';
import Template from './components/template';
import RightPanel from "./components/right-panel";
import Modal from "./components/modal";
import Certification from "./components/certification";
import DemoStepPlayback from "../demo/playback";
import {
  useDemoCalculatorPlayback,
} from "../demo/use-calculator-playback";

import {
  CALC_ID,
  getCalcId,
} from "../../../api/calc";

import {useLazyGetInformersQuery} from "../../../api/informers";
import {isDemoPage, useGetCalcIdFromRoute} from "../../../utils/hooks";
import {ROUTES} from "../../../config/constants";
import ModalBig from "../../common/modalBig";
import AlertBottom from "../../common/alertBottom";

import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';

import { CalculationReadOnlyProvider, useCalculationReadOnly } from './calculation-read-only';
import { useCalculatorPopupTheme } from './popups/use-popup-theme';
import { useCalculatorSelectBounds } from './popups/use-select-bounds';
import { resetCalculationSession } from 'utils/calculationSession';

import './../cabinet.scss'
import './calculator.scss'

type Props = {
  mainStepName?: string
  testDriveScenarioSlug?: string
  title?: React.ReactNode
  shouldHideTitle?: boolean
  shouldExpandAllSteps?: boolean
  rightPanelTopActions?: React.ReactNode
}

const EXPANDED_STEPS = {
  main: true,
  packaging: true,
  delivery: true,
  customs: true,
  certification: true,
  banking_commissions: true,
  additional_services: true,
}

const CalculatorContent = ({ mainStepName, testDriveScenarioSlug, title, shouldHideTitle, shouldExpandAllSteps, rightPanelTopActions }: Props) => {

  const { t } = useTranslation()

  const isDemoMode = isDemoPage()
  const readOnly = useCalculationReadOnly()

  const [ shouldOpenContinueModal, setShouldOpenContinueModal ] = useState(false)
  const [ draftMainName, setDraftMainName ] = useState('')

  const orderId = useGetCalcIdFromRoute()

  window.onpopstate = () => setShouldOpenContinueModal(true);

  const [ informersRequest ] = useLazyGetInformersQuery()

  const { data: orderSummary, refetch } = useGetOrderSummaryByIdQuery(orderId, { skip: !orderId })

  const [ getOrderSummaryInvoices ] = useLazyGetOrderSummaryInvoicesByIdQuery()

  const [ refreshCurrencyRates, { isLoading: isRefreshingCurrencyRates } ] = useRefreshCurrencyRatesMutation()

  const navigate = useNavigate();

  const dispatch = useDispatch()

  const calcId = getCalcId();

  const summaryCompleted = useSummaryCompleted()
  const currentRoute = useCurrentRoute()?.id
  const orderData = useOrder()
  const viewModeEditAttempt = useCalculationViewModeEditAttempt()
  const lockedByActiveApplication = Boolean(orderData?.locked_by_active_application)

  const [ expandedSteps, setExpandedSteps ] = useState<CalculatorExpandedSteps>({
    main: false,
    packaging: false,
    delivery: false,
    customs: false,
    additional_services: false,
    banking_commissions: false,
    certification: false,
  })

  useEffect(() => {
    if (calcId && !orderId) {
      setShouldOpenContinueModal(true);
    }
    informersRequest('');
    if (orderId){
      refetch();
    }
  }, [])

  useEffect(() => {
    if (isDemoMode) {
      return
    }

    if (shouldExpandAllSteps) {
      setExpandedSteps(EXPANDED_STEPS)
      return;
    }

    if (summaryCompleted) {
      setExpandedSteps({
        main: Boolean(!summaryCompleted?.common),
        packaging: Boolean(summaryCompleted?.common && !summaryCompleted?.packaging),
        delivery: Boolean(summaryCompleted?.packaging && !summaryCompleted?.delivery),
        customs: Boolean(summaryCompleted?.delivery && !summaryCompleted?.customs),
        certification: Boolean(summaryCompleted?.customs && !summaryCompleted?.certification),
        banking_commissions: Boolean(summaryCompleted?.certification && !summaryCompleted?.banking_commissions),
        additional_services: Boolean(summaryCompleted?.banking_commissions && !summaryCompleted?.additional_services)
      })
    } else if (!orderId) {
      // При создании нового расчета раскрываем шаг 1
      setExpandedSteps(prev => ({
        ...prev,
        main: true
      }))
    }
  }, [ isDemoMode, summaryCompleted, orderId, shouldExpandAllSteps ])


  const handleGoToCalculation = () => {
    setShouldOpenContinueModal(false)
    navigate(`/${ROUTES.CABINET}/${ROUTES.CALCULATOR}/${calcId}`)
  }

  const handleGoToNewCalculation = () => {
    resetCalculationSession(dispatch);
    setShouldOpenContinueModal(false)
    navigate(`/${ROUTES.CABINET}/${ROUTES.CALCULATOR}`)
  }

  const handleContinueCalcInaccessible = () => {
    localStorage.removeItem(CALC_ID);
    setShouldOpenContinueModal(false);
  }

  const shouldShowSummary = summaryCompleted?.common && summaryCompleted?.packaging &&
      summaryCompleted.delivery && summaryCompleted.customs &&
      summaryCompleted.banking_commissions &&
      summaryCompleted.certification && summaryCompleted.additional_services;

  const demoPlayback = useDemoCalculatorPlayback({
    expandedSteps,
    isDemoMode,
    setExpandedSteps,
    translate: t,
  })

  const shouldRenderSummary = demoPlayback.summaryIsVisible ?? shouldShowSummary
  const isAdditionalServicesDisabled = demoPlayback.isAdditionalServicesDisabled ??
    !summaryCompleted?.banking_commissions

  const handleAdditionalServicesComplete = () => {
    if (isDemoMode) {
      demoPlayback.changeExpandedSteps({additional_services: true})
      return
    }

    demoPlayback.changeExpandedSteps({additionalServices: true})
  }

  const handleCustomsComplete = () => {
    if (isDemoMode) {
      demoPlayback.completeCustomsStep()
      return
    }

    demoPlayback.changeExpandedSteps({customs: true, invoices: true})
  }

  const handleCertificationComplete = () => {
    if (isDemoMode) {
      demoPlayback.completeCertificationStep()
      return
    }

    demoPlayback.changeExpandedSteps({certification: false, banking_commissions: true})
  }

  const handleRefreshCurrencyRates = async () => {
    const result = await refreshCurrencyRates(orderId)

    if ('error' in result) {
      return
    }

    await Promise.all([ refetch(), getOrderSummaryInvoices(orderId) ])
  }

  const shouldShowSummaryStep = demoPlayback.isStepVisible(DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES)
  const shouldRenderCalculatorSummary = shouldRenderSummary && shouldShowSummaryStep

  const currencyRatesStatus = orderSummary?.summary?.currency_rates_status
  const shouldShowCurrencyRatesAlert = Boolean(currencyRatesStatus?.stale) && !readOnly
  const isSavedOrder = Boolean(orderId) && Number(orderData.num) === Number(orderId)
  const isMainCompleted = Boolean(summaryCompleted?.common || isSavedOrder)

  let mainName: string | undefined = mainStepName || draftMainName
  if (!mainName && isSavedOrder) mainName = orderData.order_name
  if (!mainName) mainName = t('calc__text__step1__name')

  return (
    <>
      <div
        className={cx('wrapper calculator', { 'calculator--read-only': readOnly })}
        {...demoPlayback.calculatorProps}
      >
        {!shouldHideTitle && (
          <>
            <Typography variant='h4' sx={{
              marginBottom: 3,
              letterSpacing: '-0.5px',
              fontWeight: 700
            }} id='title'>
              {title || t('calc__text__shippingCalculator')}
            </Typography>
            <CalculatorDisclaimer />
          </>
        )}
        <Template
          header={{text: t('calc__text__step1'), name: mainName}}
          isCompleted={demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.MAIN, isMainCompleted)}
          inProgress={Boolean(orderId)} id='main'
          isExpanded={expandedSteps.main}
          isExpansionControlled={isDemoMode}
          onExpandedChange={expanded => demoPlayback.changeExpandedSteps({ main: expanded })}
          onTransitionExited={demoPlayback.completeFirstStepCollapse}
        >
          <Main
            handleAdd={() => demoPlayback.changeExpandedSteps({main: true, packaging: true})}
            onDraftNameChange={setDraftMainName}
          />
        </Template>
        {demoPlayback.isStepVisible(DEMO_CALCULATOR_STEP.PACKAGING) && <Template
          header={{
            text: t('calc__text__step2'),
            name: t('calc__text__step2__name'),
          }}
          isCompleted={demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.PACKAGING, summaryCompleted?.packaging)}
          id='packaging'
          // isDisabled={!summaryCompleted?.common}
          isExpanded={expandedSteps.packaging}
          isExpansionControlled={isDemoMode}
          onExpandedChange={expanded => demoPlayback.changeExpandedSteps({ packaging: expanded })}
        >
          <InvoicesAndProducts
            testDriveScenarioSlug={testDriveScenarioSlug}
            handleComplete={() =>  demoPlayback.changeExpandedSteps({packaging: true, delivery: true})}
          />
          {demoPlayback.getStepButtons([ DEMO_CALCULATOR_STEP.MAIN, DEMO_CALCULATOR_STEP.PACKAGING ], DEMO_CALCULATOR_STEP.DELIVERY)}
        </Template>}
        {demoPlayback.isStepVisible(DEMO_CALCULATOR_STEP.DELIVERY) && <Template
          header={{text: t('calc__text__step3'), name: t('calc__text__step3__name')}}
          isCompleted={demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.DELIVERY, summaryCompleted?.delivery)}
          id='delivery'
          isDisabled={!demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.PACKAGING, summaryCompleted?.packaging)}
          isExpanded={expandedSteps.delivery}
          isExpansionControlled={isDemoMode}
          onExpandedChange={expanded => demoPlayback.changeExpandedSteps({ delivery: expanded })}
        >
          <Delivery handleChoose={() =>  demoPlayback.changeExpandedSteps({delivery: true, customs: true})}/>
          {demoPlayback.getStepButtons([ DEMO_CALCULATOR_STEP.DELIVERY ], DEMO_CALCULATOR_STEP.CUSTOMS)}
        </Template>}
        {demoPlayback.isStepVisible(DEMO_CALCULATOR_STEP.CUSTOMS) && <Template
          header={{text: t('calc__text__step4'), name: t('calc__text__step4__name')}}
          isCompleted={demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.CUSTOMS, summaryCompleted?.customs)}
          id='customs'
          isDisabled={!demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.DELIVERY, summaryCompleted?.delivery)}
          isExpanded={demoPlayback.shouldExpandCustoms}
          isExpansionControlled={isDemoMode}
          onExpandedChange={expanded => demoPlayback.changeExpandedSteps({ customs: expanded })}
        >
          {summaryCompleted?.packaging && <Customs
            handleComplete={handleCustomsComplete}
          />}
          {demoPlayback.getStepButtons([ DEMO_CALCULATOR_STEP.CUSTOMS ], DEMO_CALCULATOR_STEP.CERTIFICATION)}
        </Template>}
        {demoPlayback.isStepVisible(DEMO_CALCULATOR_STEP.CERTIFICATION) && <Template
          header={{text: t('calc__text__step5'), name: t('calc__text__step5__name')}}
          id={demoPlayback.getStepId(DEMO_CALCULATOR_STEP.CERTIFICATION, 'invoices')}
          isCompleted={demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.CERTIFICATION, summaryCompleted?.certification)}
          isDisabled={!demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.CUSTOMS, summaryCompleted?.customs)}
          isExpanded={expandedSteps.certification}
          isExpansionControlled={isDemoMode}
          onExpandedChange={expanded => demoPlayback.changeExpandedSteps({ certification: expanded })}
        >
          {summaryCompleted?.customs && <Certification
            handleComplete={handleCertificationComplete}
          />}
          {demoPlayback.getStepButtons([ DEMO_CALCULATOR_STEP.CERTIFICATION ], DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS)}
        </Template>}
        {demoPlayback.isStepVisible(DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS) && <Template
          header={{text: t('calc__text__step6'), name: t('calc__text__step6__name')}}
          id={demoPlayback.getStepId(DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS, 'invoices')}
          isCompleted={demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS, summaryCompleted?.banking_commissions)}
          isDisabled={!demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.CERTIFICATION, summaryCompleted?.certification)}
          isExpanded={expandedSteps.banking_commissions}
          isExpansionControlled={isDemoMode}
          onExpandedChange={expanded => demoPlayback.changeExpandedSteps({ banking_commissions: expanded })}
        >
          {summaryCompleted?.customs && <BankCommission
            handleComplete={() => demoPlayback.changeExpandedSteps({invoices: true, additional_services: true})}
          />}
          {demoPlayback.getStepButtons([ DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS ], DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES)}
        </Template>}
        {demoPlayback.isStepVisible(DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES) && <Template
          header={{text: t('calc__text__step7'), name: t('calc__text__step7__name')}}
          id={demoPlayback.getStepId(DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES, 'additional')}
          isCompleted={demoPlayback.isStepCompleted(DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES, summaryCompleted?.additional_services)}
          isDisabled={isAdditionalServicesDisabled}
          isExpanded={expandedSteps.additional_services}
          isExpansionControlled={isDemoMode}
          onExpandedChange={expanded => demoPlayback.changeExpandedSteps({ additional_services: expanded })}
        >
          {currentRoute && <Additional
            handleComplete={handleAdditionalServicesComplete}
          />}
          {demoPlayback.shouldShowAdditionalServicesNext && (
            <div className='calculator__demo-step-buttons'>
              <Button
                data-demo-allowed='next-step'
                className='calculator__demo-step-button'
                variant='contained'
                onClick={demoPlayback.showSummary}
              >
                {t('actions__text__next')}
              </Button>
            </div>
          )}
        </Template>}
        {shouldRenderCalculatorSummary && <Summary/>}
        {isDemoMode && <DemoStepPlayback {...demoPlayback.playbackProps} />}
      </div>
      <RightPanel
        topActions={rightPanelTopActions}
        {...demoPlayback.rightPanelProps}
      />
      {shouldOpenContinueModal && calcId && <ModalBig
        isOpen={shouldOpenContinueModal}
        onClose={() => setShouldOpenContinueModal(false)}
        title={t('calc__text__modal__continuationOfTheCalculation')}
      >
        <Modal
          handleYes={handleGoToCalculation}
          handleNo={handleGoToNewCalculation}
          id={calcId}
          onInaccessible={handleContinueCalcInaccessible}
        />
      </ModalBig>}
      <AlertBottom
        open={!isDemoMode && lockedByActiveApplication}
        title={t('calc__text__lockedByApplication')}
        message={t('calc__message__lockedByApplication')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={null}
        noCloseButton
      />
      <AlertBottom
        open={viewModeEditAttempt}
        onClose={() => dispatch(clearCalculationViewModeEditAttempt())}
        message={t(lockedByActiveApplication ? 'calc__message__lockedByApplication' : 'calc__message__viewMode')}
        isError
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
      <AlertBottom
        open={shouldShowCurrencyRatesAlert}
        title={t('calc__text__summary__currencyRatesChanged')}
        message={t('calc__text__summary__currencyRatesChangedMessage')}
        action={currencyRatesStatus?.can_refresh ? (
          <Button
            color='inherit'
            className={cx({ 'read-only-action-button': readOnly })}
            disabled={readOnly || isRefreshingCurrencyRates}
            onClick={readOnly ? undefined : handleRefreshCurrencyRates}
            title={readOnly ? t('calc__message__lockedByApplication') : undefined}
            endIcon={readOnlyButtonEndIcon(readOnly)}
          >
            {t('actions__text__recalculate')}
          </Button>
        ) : undefined}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={null}
        noCloseButton
      />
    </>
  )
}

const Calculator = (props: Props) => {
  const shouldAdjustPopups = !isDemoPage()
  const popupTheme = useCalculatorPopupTheme(shouldAdjustPopups)
  useCalculatorSelectBounds(shouldAdjustPopups)

  return (
    <ThemeProvider theme={popupTheme}>
      <CalculationReadOnlyProvider>
        <CalculatorContent {...props} />
      </CalculationReadOnlyProvider>
    </ThemeProvider>
  )
};

export default Calculator
